import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { SupportTicket } from '../data-models/support-ticket.model';
import { SupportTicketAttachment } from '../data-models/support-ticket-attachment.model';
import { SupportTicketThread } from '../data-models/support-ticket-thread.model';
import { SupportTicketCreate } from '../request-models/support-ticket-create.model';
import { SupportTicketListQuery } from '../request-models/support-ticket-list-query.model';
import { SupportTicketStatusRequest } from '../request-models/support-ticket-status-request.model';
import { SupportTicketThreadRequest } from '../request-models/support-ticket-thread-request.model';
import { SupportTicketWithdrawRequest } from '../request-models/support-ticket-withdraw-request.model';
import { SupportTicketAttachmentService } from './support-ticket-attachment.service';
import { SupportTicketThreadService } from './support-ticket-thread.service';
import { Page } from '../response-models/page.model';
import { SupportTicketAttachmentUploadResult } from '../response-models/support-ticket-attachment-upload-result.model';
import { SupportTicketStatusCounts } from '../response-models/support-ticket-status-counts.model';
import { isRecord } from '../utils/is-record';
import { toSupportTicketCreateRequest, toSupportTicketListQuery } from '../utils/support-ticket-request-utils';
import { countOrZero } from '../utils/wire-value-utils';

/**
 * Support tickets: `/support-tickets/*` (hub `user-support-tickets-controller.ts`). The `:id/threads` and
 * `:id/attachments` sub-resources have their own nested services; this service keeps the ticket-level API
 * for them so pages use one entry point.
 *
 * Every method rejects with an `ApiError`; a Core org gets `code === 'pro_required'`.
 */
@Injectable({
  providedIn: 'root'
})
export class SupportTicketService extends BaseService<SupportTicket> {
  private threadService = inject(SupportTicketThreadService);
  private attachmentService = inject(SupportTicketAttachmentService);

  constructor() {
    super('support-tickets', SupportTicket);
  }

  /** Create a support ticket. */
  async createTicket(data: SupportTicketCreate): Promise<SupportTicket> {
    return required(await firstValueFrom(this.create(toSupportTicketCreateRequest(data))));
  }

  /** One page of an organization's tickets, with optional filters (`page` / `limit` paging). */
  async getOrganizationTickets(organizationId: string, query?: SupportTicketListQuery): Promise<Page<SupportTicket>> {
    const opts = { path: ['organization', organizationId], query: toSupportTicketListQuery(query) };
    return firstValueFrom(this.getPage(opts));
  }

  /** Ticket counts per status for an organization. */
  async getOrganizationTicketCounts(organizationId: string): Promise<SupportTicketStatusCounts> {
    const opts = { path: ['organization', organizationId, 'counts'] };
    return firstValueFrom(this.request('GET', opts, toStatusCounts));
  }

  /** One ticket, or `null` when the hub returns none. */
  async getTicket(id: string): Promise<SupportTicket | null> {
    return firstValueFrom(this.get(id));
  }

  /** Resolve, close or reopen a ticket. */
  async updateTicketStatus(id: string, status: SupportTicketStatusRequest['status']): Promise<SupportTicket> {
    const body: SupportTicketStatusRequest = { status };
    return required(await firstValueFrom(this.request('PATCH', { id, path: 'status' }, data => this.toTicket(data), body)));
  }

  /**
   * Withdraw (close) a ticket with a reason. The hub keeps the row and syncs the closure to Zoho Desk.
   *
   * @param reason 'created_by_mistake' | 'resolved_on_my_own' | 'duplicate_ticket' | 'no_longer_needed' | 'other'
   * @param details Optional additional details (used when reason is 'other')
   */
  async deleteTicket(id: string, reason: string = 'no_longer_needed', details?: string): Promise<void> {
    const body: SupportTicketWithdrawRequest = details === undefined ? { reason } : { reason, details };
    await firstValueFrom(this.request('DELETE', { id }, () => undefined, body));
  }

  /** Tickets linked to a drone (`[]` when the drone has none). */
  async getDroneTickets(droneId: string): Promise<SupportTicket[]> {
    return firstValueFrom(this.getMany({ path: ['drone', droneId] }));
  }

  /** The ticket's conversation history. */
  async getTicketThreads(ticketId: string): Promise<SupportTicketThread[]> {
    return firstValueFrom(this.threadService.listForTicket(ticketId));
  }

  /** Add a public reply to a ticket. */
  async addTicketThread(ticketId: string, content: string): Promise<SupportTicketThread> {
    const body: SupportTicketThreadRequest = { content, isPublic: true, threadType: 'reply' };
    const thread = await firstValueFrom(this.threadService.addToTicket(ticketId, body));
    if (!thread) {
      throw new Error('The hub returned no thread');
    }
    return thread;
  }

  /** The ticket's attachments. */
  async getTicketAttachments(ticketId: string): Promise<SupportTicketAttachment[]> {
    return firstValueFrom(this.attachmentService.listForTicket(ticketId));
  }

  /** Upload files to a ticket. Files the hub rejects are listed in `errors`; the rest are in `attachments`. */
  async uploadAttachments(ticketId: string, files: File[]): Promise<SupportTicketAttachmentUploadResult> {
    return firstValueFrom(this.attachmentService.uploadToTicket(ticketId, files));
  }

  /**
   * An attachment's file as a `Blob`, through the hub's authenticated download route (used for Zoho Desk
   * attachments, whose `downloadUrl` is that relative hub route).
   */
  async downloadAttachment(ticketId: string, attachmentId: string): Promise<Blob> {
    return firstValueFrom(this.attachmentService.downloadFromTicket(ticketId, attachmentId));
  }

  private toTicket(data: unknown): SupportTicket | null {
    return data === null || data === undefined ? null : this.createType(data);
  }
}

function required(ticket: SupportTicket | null): SupportTicket {
  if (!ticket) {
    throw new Error('The hub returned no support ticket');
  }
  return ticket;
}

function toStatusCounts(data: unknown): SupportTicketStatusCounts {
  const source = isRecord(data) ? data : {};
  return {
    open: countOrZero(source['open']),
    in_progress: countOrZero(source['in_progress']),
    resolved: countOrZero(source['resolved']),
    closed: countOrZero(source['closed']),
  };
}
